@let canLogistViewDeviceLink =
  permissionService.canLogistViewDeviceLink$ | async;

<div class="device">
  <div class="flex flex-row gap-3">
    <div class="grow flex flex-row gap-2">
      <span
        tuiBadge
        class="rounded-lg! bg-(--tui-background-accent-opposite-hover)!"
        appearance="custom"
        >{{ $orderNumber() }}</span
      >
      <div class="inline-block leading-(--tui-height-xs)">
        <span
          class="inline-block px-2 rounded-lg bg-(--tui-background-neutral-1-hover) text-(--tui-background-accent-opposite)! leading-(--tui-height-xs)"
          >{{ deviceName }}</span
        >
      </div>
      @if (device.hasProtectCase) {
        <tui-icon
          icon="@tui.lock"
          [tuiHint]="protectCaseTooltip"
          [tuiHintShowDelay]="250"
          class="size-6 text-base"
        ></tui-icon>
        <ng-template #protectCaseTooltip>
          <div>
            {{
              'request_form.devices.device.tooltip_device_in_protective_case'
                | translate
            }}
          </div>
        </ng-template>
      }
    </div>
    <div class="flex flex-col text-xs x-text-07">
      <div class="w-fit">{{ device.installDate | date: 'dd.MM.yyyy' }}</div>
      <div class="w-fit">
        {{ 'request_form.devices.device.installed' | translate }}
      </div>
    </div>
  </div>

  <div class="flex flex-row gap-3 mt-3">
    <div class="flex-1">
      <div class="text-xs x-text-07">
        {{ 'request_form.devices.device.sim_1' | translate }}
      </div>
      <div class="flex flex-row items-center">
        @if (canLogistViewDeviceLink) {
          <a
            href="https://panel.x-keeper.net/#/signin?login={{
              device.name
            }}&key={{ device.key }}"
            target="_blank"
            tuiLink
            >{{ device.name }}</a
          >
        } @else {
          {{ device.name }}
        }
        <x-text-copier
          [textToCopy]="device.name"
          size="xs"
          class="ml-3"
        ></x-text-copier>
      </div>
    </div>
    <div class="flex-1">
      <div class="text-xs x-text-07">
        {{ 'request_form.devices.device.sim_2' | translate }}
      </div>
      @if (device.secondSim) {
        <div class="flex flex-row items-center">
          @if (canLogistViewDeviceLink) {
            <a
              href="https://panel.x-keeper.net/#/signin?login={{
                device.secondSim
              }}&key={{ device.key }}"
              target="_blank"
              tuiLink
            >
              {{ device.secondSim }}
            </a>
          } @else {
            {{ device.secondSim }}
          }
          <x-text-copier
            [textToCopy]="device.secondSim"
            size="xs"
            class="ml-3"
          ></x-text-copier>
        </div>
      } @else {
        <div>{{ 'request_form.devices.device.no_sim_2' | translate }}</div>
      }
    </div>
  </div>

  @if (permissionService.canLogistViewScheduleOwners$ | async) {
    <div class="flex flex-row gap-3 mt-3">
      <div class="flex-1">
        <div class="text-xs x-text-07">
          {{ 'request_form.devices.device.profile' | translate }}
        </div>
        <div>
          @if (device.profile) {
            {{ device.profile.id }} {{ device.profile.name }}
          } @else {
            {{ 'request_form.devices.device.profile_undefined' | translate }}
          }
        </div>
      </div>
      <div class="flex-1">
        <div class="text-xs x-text-07">
          {{ 'request_form.devices.device.events' | translate }}
        </div>
        <div>
          {{
            device.status?.extra?.events_count ||
              ('request_form.devices.device.events_undefined' | translate)
          }}
        </div>
      </div>
    </div>
  }

  <div class="flex flex-row gap-3 mt-3">
    <div class="flex-1">
      <div class="text-xs x-text-07">
        {{ 'request_form.devices.device.last_device_data' | translate }}
      </div>
      <div class="flex flex-col">
        {{
          lastDeviceDatetime
            ? formatDate(lastDeviceDatetime)
            : ('request_form.devices.device.last_device_data_undefined'
              | translate)
        }}
        @if (daysFromLastData !== null) {
          @if (daysFromLastData > 0) {
            <span
              tuiChip
              [appearance]="daysFromLastData === 1 ? 'warning' : 'negative'"
              class="max-w-fit!"
              >{{ daysFromLastData }}
              {{ 'request_form.devices.device.days_ago' | translate }}</span
            >
          } @else {
            <span tuiChip appearance="primary" class="max-w-fit!">{{
              'request_form.devices.device.today_tag' | translate
            }}</span>
          }
        }
      </div>
    </div>
    <div class="flex-1">
      <div class="text-xs x-text-07">
        {{ 'request_form.devices.device.coordinates' | translate }}
        @if (coordinates) {
          <a
            href="https://maps.yandex.ru?text={{ coordinates }}"
            target="_blank"
            tuiLink
          >
            <tui-icon
              icon="@tui.external-link"
              class="size-6 text-base scale-75 relative bottom-[2px] right-1"
            ></tui-icon>
          </a>
        }
      </div>
      <div>
        @if (coordinates) {
          <div>{{ coordinates }}</div>
          @if (lastLocationDatetime) {
            <div>{{ formatDate(lastLocationDatetime) }}</div>
          }
          @if (
            daysBetweenLocationAndLastDate && daysBetweenLocationAndLastDate > 0
          ) {
            <div>
              {{ 'request_form.devices.device.before' | translate }}
              {{ daysBetweenLocationAndLastDate }}
              {{
                'request_form.devices.device.days_before_last_data' | translate
              }}
            </div>
          }
        } @else {
          <div>
            {{
              'request_form.devices.device.coordinates_undefined' | translate
            }}
          </div>
        }
        <x-device-location-updater
          [device]="device"
          (deviceLocationUpdated)="deviceDataUpdated.emit(device)"
        ></x-device-location-updater>
      </div>
    </div>
  </div>

  <div class="flex flex-row gap-3 mt-3">
    <div class="flex-1">
      <div class="text-xs x-text-07">
        {{ 'request_form.devices.device.address' | translate }}
      </div>
      <div>
        @if (device.status?.address?.ru) {
          <div>{{ device.status?.address?.ru }}</div>
          @if (device.status?.address_dt) {
            {{ formatDate(device.status!.address_dt, true) }}
          }
        } @else {
          {{ 'request_form.devices.device.address_undefined' | translate }}
        }
      </div>
    </div>
    <div class="flex-1">
      @if (canLogistViewDeviceLink) {
        @if (device.mountType) {
          <div class="mt-3">
            <div class="text-xs x-text-07">
              {{ 'request_form.devices.device.mount_type' | translate }}
            </div>
            <p>
              {{ device.mountType }}
            </p>
          </div>
        }
        @if (device.onboardVoltage) {
          <div
            class="mt-3"
            [ngClass]="{
              absolute:
                device.mountType &&
                device.onboardVoltage &&
                device.cutoffSwitch === undefined,
            }"
          >
            <div class="text-xs x-text-07">
              {{ 'request_form.devices.device.onboard_voltage' | translate }}
            </div>
            <p>{{ device.onboardVoltage }}</p>
          </div>
        }
        @if (device.cutoffSwitch !== undefined) {
          <div
            class="mt-3"
            [ngClass]="{
              absolute:
                device.cutoffSwitch !== undefined &&
                (device.mountType || device.onboardVoltage),
            }"
          >
            <div class="text-xs x-text-07">
              {{ 'request_form.devices.device.cutoff_switch' | translate }}
            </div>
            <p>
              {{
                getCutoffSwitchTranslationKey(device.cutoffSwitch) | translate
              }}
            </p>
          </div>
        }
      }
    </div>
  </div>

  @if (permissionService.canViewStatus$ | async) {
    <div class="mt-3">
      <div class="text-xs x-text-07">
        {{ 'request_form.devices.device.device_hash' | translate }}
      </div>
      <div>
        <a
          href="https://system.x-keeper.net/system/#/status?id={{
            device.deviceHash
          }}"
          target="_blank"
          tuiLink
          >{{ device.deviceHash }}</a
        >
      </div>
    </div>
  }

  <div class="flex flex-row gap-3 mt-3">
    @if (permissionService.canViewInstallQuality$ | async) {
      @let installQualityStatus = installQualityStatus$ | async;
      <div class="flex-1">
        <div class="text-xs x-text-07">
          {{
            'request_form.devices.device.install_quality.install_quality'
              | translate
          }}
          @if (
            installQualityStatus?.lastRequest &&
            !installQualityStatus?.lastRequest?.finished
          ) {
            <span tuiBadge tuiStatus size="s" appearance="info" class="ml-1">{{
              'request_form.devices.device.install_quality.check_in_progress'
                | translate
            }}</span>
          }
        </div>
        <div class="mt-1">
          @switch (
            installQualityStatus?.lastCheck?.installation_quality_score
          ) {
            @case ('bad') {
              <span tuiBadge tuiStatus appearance="negative">{{
                'request_form.devices.device.install_quality.bad' | translate
              }}</span>
            }
            @case ('gps_good') {
              <span tuiBadge tuiStatus appearance="positive">{{
                'request_form.devices.device.install_quality.gps_good'
                  | translate
              }}</span>
            }
            @case ('gsm_or_wifi_good') {
              <span tuiBadge tuiStatus appearance="positive">{{
                'request_form.devices.device.install_quality.gsm_or_wifi_good'
                  | translate
              }}</span>
            }
            @case (null) {
              @if (installQualityStatus?.lastRequest?.finished) {
                <span tuiBadge tuiStatus appearance="negative">{{
                  'request_form.devices.device.install_quality.timed_out'
                    | translate
                }}</span>
              }
            }
          }
          <div class="text-xs mt-1">
            @switch (
              installQualityStatus?.lastCheck?.installation_location_type
            ) {
              @case ('sls_recorded_location') {
                {{
                  'request_form.devices.device.install_quality.by_browser_coords'
                    | translate
                }}
              }
              @case ('sls_verified_address') {
                {{
                  'request_form.devices.device.install_quality.by_actual_delivery_address'
                    | translate
                }}
                ({{
                  installQualityStatus?.lastCheck
                    ?.installation_location_title ||
                    ('request_form.devices.device.install_quality.address_unavailable'
                      | translate)
                }})
              }
              @case ('declared_address') {
                <div class="text-xs">
                  {{
                    'request_form.devices.device.install_quality.by_delivery_address'
                      | translate
                  }}
                  ({{
                    installQualityStatus?.lastCheck
                      ?.installation_location_title ||
                      ('request_form.devices.device.install_quality.address_unavailable'
                        | translate)
                  }})
                </div>
              }
            }
          </div>
          @if (installQualityStatus?.lastCheck) {
            <div class="text-xs text-gray-500">
              {{
                'request_form.devices.device.install_quality.checked_at'
                  | translate
              }}
              {{
                installQualityStatus!.lastCheck!.create_dt
                  | date: 'dd.MM.yyyy HH:mm:ss'
              }}
            </div>
          } @else {
            <div class="text-xs text-gray-500">
              {{
                'request_form.devices.device.install_quality.never_checked'
                  | translate
              }}
            </div>
          }
        </div>

        <button
          tuiButton
          type="button"
          size="s"
          appearance="primary"
          class="mt-2"
          (click)="scheduleInstallQualityCheck()"
          [disabled]="
            (installQualityStatus?.lastRequest &&
              !installQualityStatus?.lastRequest?.finished) ??
            false
          "
          [tuiHint]="
            installQualityStatus?.lastRequest &&
            !installQualityStatus?.lastRequest?.finished
              ? ('request_form.devices.device.install_quality.check_in_progress'
                | translate)
              : null
          "
        >
          {{
            'request_form.devices.device.install_quality.check_now' | translate
          }}
        </button>
      </div>

      <!-- Блок тампера -->
      <div class="flex-1">
        <div class="text-xs x-text-07">
          {{ 'request_form.devices.device.tamper.title' | translate }}
        </div>

        @if (tamperLoadState() === 'loading' && !tamperStatus()) {
          <span tuiBadge tuiStatus size="s" appearance="info" class="ml-1">{{
            'request_form.devices.device.tamper.loading' | translate
          }}</span>
        }

        @if (
          tamperLoadState() === 'not_found' || tamperLoadState() === 'error'
        ) {
          <span
            tuiBadge
            tuiStatus
            size="s"
            appearance="negative"
            class="ml-1"
            >{{ 'request_form.devices.device.tamper.error' | translate }}</span
          >
        }

        @if (
          tamperLoadState() === 'loaded' &&
          (!tamperStatus() || tamperStatus()?.isMounted === null)
        ) {
          <span tuiBadge tuiStatus size="s" appearance="warning" class="ml-1">{{
            'request_form.devices.device.tamper.no_data' | translate
          }}</span>
        }

        @if (tamperLoadState() === 'loaded' && tamperStatus()?.isMounted) {
          <span
            tuiBadge
            tuiStatus
            size="s"
            appearance="positive"
            class="mt-1"
            >{{
              'request_form.devices.device.tamper.mounted' | translate
            }}</span
          >
        }

        @if (
          tamperLoadState() === 'loaded' && tamperStatus()?.isMounted === false
        ) {
          <span
            tuiBadge
            tuiStatus
            size="s"
            appearance="negative"
            class="mt-1"
            >{{
              'request_form.devices.device.tamper.not_mounted' | translate
            }}</span
          >
        }

        @if (tamperLoadState() === 'loaded' && tamperStatus()?.tamperDt) {
          <div class="text-xs text-gray-500 mt-1">
            {{ tamperStatus()?.tamperDt | tamperDtFormatted }}
          </div>
        }
      </div>
    }
  </div>

  @if (permissionService.canLogistAddDevices$ | async) {
    <div class="flex justify-end mt-3 gap-5">
      <button
        tuiIconButton
        type="button"
        size="s"
        (click)="editRequestDeviceButtonClicked.emit(device)"
        appearance="transparent"
      >
        <tui-icon
          [style.border-width.rem]="0.25"
          icon="@tui.pencil"
          class="x-text-01 text-2xl!"
        ></tui-icon>
      </button>

      <x-request-device-remover
        [device]="device"
        (requestDeviceRemoved)="requestDeviceRemoved.emit(device)"
      ></x-request-device-remover>
    </div>
  }
</div>
